/**
 * collection-filter-bar.js
 *
 * Sidebar filter panel — no DOM moving.
 * The sidebar and product grid sit in a permanent flex row.
 * The sidebar slides in/out via width transition.
 * Grid column count is fixed — set once by Liquid and never changed by JS.
 */

(function () {
  'use strict';

  const dataEl = document.getElementById('filter-data');
  if (!dataEl) return;

  const { groups, priceRange } = JSON.parse(dataEl.textContent);

  /* ── DOM refs ── */
  const toggleBtn   = document.getElementById('filter-toggle-btn');
  const sidebar     = document.getElementById('filter-sidebar');
  const wrapper     = document.getElementById('collection-with-filters');
  const clearBtn    = document.getElementById('filter-clear');
  const resultsEl   = document.getElementById('filter-results-count');
  const productGrid = document.getElementById('product-grid');

  if (!toggleBtn || !sidebar || !wrapper || !productGrid) return;

  /* ── State ── */
  // activeFilters: { groupName: Set<filterName> } — multi-select per group
  const activeFilters = {};
  groups.forEach(g => { activeFilters[g.name] = new Set(); });
  let sidebarOpen = false;

  const PRICE_MIN = priceRange ? priceRange.min : 0;
  const PRICE_MAX = priceRange ? priceRange.max : 0;
  let priceMinVal  = PRICE_MIN;
  let priceMaxVal  = PRICE_MAX;

  // The server-rendered markup always starts closed (no is-open/is-active
  // classes — see collection-filter-bar.liquid) so mobile never flashes an
  // open sidebar before this runs. Desktop opens it by default here;
  // mobile stays closed until the user taps the filter button.
  // Column count is never touched here — it stays at whatever Liquid
  // rendered it as (data-default-columns), regardless of sidebar state.
  if (window.innerWidth > 749) {
    openSidebar();
  }

  /* ── Sidebar toggle ── */
  function openSidebar() {
    sidebarOpen = true;
    sidebar.classList.add('is-open');
    toggleBtn.classList.add('is-active');
    toggleBtn.setAttribute('aria-expanded', 'true');
    // Cache layout after sidebar transition completes
    setTimeout(() => { cacheSidebarLayout(); updateStickyPosition(); }, 320);
  }

  function closeSidebar() {
    sidebarOpen = false;
    sidebar.classList.remove('is-open');
    toggleBtn.classList.remove('is-active');
    toggleBtn.setAttribute('aria-expanded', 'false');
  }

  toggleBtn.addEventListener('click', () => {
    sidebarOpen ? closeSidebar() : openSidebar();
  });

  /* ── Filter cards ── */
  function getCards() {
    return [...document.querySelectorAll('#product-grid [data-filter-names]')];
  }

  /* ── Card matching helpers ── */

  function cardMatchesFilters(card, excludeGroup) {
    // Price check
    const priceActive = priceMinVal > PRICE_MIN || priceMaxVal < PRICE_MAX;
    if (priceActive) {
      const cardMin = parseFloat(card.dataset.priceMin || '0');
      const cardMax = parseFloat(card.dataset.priceMax || '0');
      if (!(cardMax >= priceMinVal && cardMin <= priceMaxVal)) return false;
    }

    const names = new Set(
      (card.dataset.filterNames || '').split('|').map(n => n.trim()).filter(Boolean)
    );

    // For each group (except the excluded one), card must match at least one selected value (OR within group)
    // All groups must match (AND across groups)
    for (const [groupName, selected] of Object.entries(activeFilters)) {
      if (groupName === excludeGroup) continue;
      if (selected.size === 0) continue;
      // Card must have at least one of the selected values for this group
      let groupMatch = false;
      for (const val of selected) {
        if (names.has(val)) { groupMatch = true; break; }
      }
      if (!groupMatch) return false;
    }
    return true;
  }

  function render() {
    const anyFilterActive = Object.values(activeFilters).some(s => s.size > 0);
    const priceActive     = priceMinVal > PRICE_MIN || priceMaxVal < PRICE_MAX;
    const anyActive       = anyFilterActive || priceActive;
    const cards           = getCards();

    // Show/hide product cards
    cards.forEach(card => {
      if (!anyActive) { card.hidden = false; return; }
      card.hidden = !cardMatchesFilters(card, null);
    });

    // Update each option: count, selected state, greyed-out state
    document.querySelectorAll('.filter-group').forEach(group => {
      const groupName   = group.dataset.groupName;
      const selectedSet = activeFilters[groupName];

      group.querySelectorAll('.filter-group__option').forEach(opt => {
        const fname      = opt.dataset.filterName;
        const isSelected = selectedSet && selectedSet.has(fname);

        opt.setAttribute('aria-selected', isSelected ? 'true' : 'false');
        opt.classList.toggle('is-selected', isSelected);

        // Count products that would match the other groups' active filters
        // AND have this specific option — test each option independently,
        // not combined with currently selected options in the same group
        const testSet = new Set([fname]); // only this option, not the full selectedSet

        let count = 0;
        cards.forEach(card => {
          const realSet = activeFilters[groupName];
          activeFilters[groupName] = testSet;
          const matches = cardMatchesFilters(card, null);
          activeFilters[groupName] = realSet;
          if (matches) count++;
        });

        // Update count badge
        let badge = opt.querySelector('.filter-option__count');
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'filter-option__count';
          opt.appendChild(badge);
        }
        badge.textContent = `(${count})`;

        // Grey out (not hide) if count is 0 and option is not already selected
        opt.classList.toggle('is-disabled', count === 0 && !isSelected);
        opt.setAttribute('aria-disabled', count === 0 && !isSelected ? 'true' : 'false');
        opt.style.pointerEvents = count === 0 && !isSelected ? 'none' : '';
        opt.hidden = false; // never hide — always show greyed out
      });

      // Move disabled options to the bottom of the list
      const optionList = group.querySelector('.filter-group__options');
      if (optionList) {
        const allOpts = [...optionList.querySelectorAll('.filter-group__option')];
        const enabled  = allOpts.filter(o => !o.classList.contains('is-disabled'));
        const disabled = allOpts.filter(o =>  o.classList.contains('is-disabled'));
        [...enabled, ...disabled].forEach(o => optionList.appendChild(o));
      }
    });

    if (clearBtn) clearBtn.style.display = anyActive ? 'block' : 'none';

    if (resultsEl) {
      if (anyActive) {
        const n = cards.filter(c => !c.hidden).length;
        resultsEl.textContent = `${n} product${n === 1 ? '' : 's'} found`;
      } else {
        resultsEl.textContent = '';
      }
    }

    // Re-run sticky so max-height recalculates against the new last visible product
    requestAnimationFrame(updateStickyPosition);
  }

  /* ── Price filter: slider + inputs ── */
  const priceSlider    = document.getElementById('filter-price-slider');
  const priceRangeEl    = document.getElementById('filter-price-range');
  const handleMin       = document.getElementById('filter-price-handle-min');
  const handleMax       = document.getElementById('filter-price-handle-max');
  const inputMin        = document.getElementById('filter-price-min-input');
  const inputMax        = document.getElementById('filter-price-max-input');

  function priceToPercent(value) {
    if (PRICE_MAX === PRICE_MIN) return 0;
    return ((value - PRICE_MIN) / (PRICE_MAX - PRICE_MIN)) * 100;
  }

  function percentToPrice(percent) {
    const raw = PRICE_MIN + (percent / 100) * (PRICE_MAX - PRICE_MIN);
    return Math.round(raw);
  }

  function updatePriceUI() {
    if (!priceSlider || !handleMin || !handleMax || !priceRangeEl) return;

    const minPercent = priceToPercent(priceMinVal);
    const maxPercent = priceToPercent(priceMaxVal);

    handleMin.style.left = `${minPercent}%`;
    handleMax.style.left = `${maxPercent}%`;
    priceRangeEl.style.left  = `${minPercent}%`;
    priceRangeEl.style.right = `${100 - maxPercent}%`;

    handleMin.setAttribute('aria-valuenow', Math.round(priceMinVal));
    handleMax.setAttribute('aria-valuenow', Math.round(priceMaxVal));

    if (inputMin && document.activeElement !== inputMin) {
      inputMin.value = Math.round(priceMinVal);
    }
    if (inputMax && document.activeElement !== inputMax) {
      inputMax.value = Math.round(priceMaxVal);
    }
  }

  function setPriceMin(value, fromInput) {
    const MIN_GAP = Math.max(1, Math.round((PRICE_MAX - PRICE_MIN) * 0.01)); // 1% gap, min £1
    value = Math.max(PRICE_MIN, Math.min(value, priceMaxVal - MIN_GAP));
    priceMinVal = value;
    updatePriceUI();
    if (!fromInput) render();
  }

  function setPriceMax(value, fromInput) {
    const MIN_GAP = Math.max(1, Math.round((PRICE_MAX - PRICE_MIN) * 0.01)); // 1% gap, min £1
    value = Math.min(PRICE_MAX, Math.max(value, priceMinVal + MIN_GAP));
    priceMaxVal = value;
    updatePriceUI();
    if (!fromInput) render();
  }

  /* ── Slider drag handling ── */
  function startDrag(handle, isMin) {
    // Bring the active handle to the front so it stays grabbable when overlapping
    if (handleMin) handleMin.style.zIndex = isMin ? '4' : '3';
    if (handleMax) handleMax.style.zIndex = isMin ? '3' : '4';

    function onMove(clientX) {
      const rect    = priceSlider.getBoundingClientRect();
      let percent   = ((clientX - rect.left) / rect.width) * 100;
      percent       = Math.max(0, Math.min(100, percent));
      const value   = percentToPrice(percent);

      if (isMin) {
        setPriceMin(value);
      } else {
        setPriceMax(value);
      }
    }

    function onMouseMove(e) { onMove(e.clientX); }
    function onTouchMove(e) {
      if (e.touches && e.touches[0]) onMove(e.touches[0].clientX);
    }

    function stopDrag() {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', stopDrag);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', stopDrag);
      render();
      scrollToGrid();
    }

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', stopDrag);
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', stopDrag);
  }

  if (handleMin) {
    handleMin.addEventListener('mousedown', () => startDrag(handleMin, true));
    handleMin.addEventListener('touchstart', () => startDrag(handleMin, true), { passive: true });
    handleMin.addEventListener('keydown', e => {
      if (e.key === 'ArrowLeft')  { setPriceMin(priceMinVal - 1); render(); }
      if (e.key === 'ArrowRight') { setPriceMin(priceMinVal + 1); render(); }
    });
  }

  if (handleMax) {
    handleMax.addEventListener('mousedown', () => startDrag(handleMax, false));
    handleMax.addEventListener('touchstart', () => startDrag(handleMax, false), { passive: true });
    handleMax.addEventListener('keydown', e => {
      if (e.key === 'ArrowLeft')  { setPriceMax(priceMaxVal - 1); render(); }
      if (e.key === 'ArrowRight') { setPriceMax(priceMaxVal + 1); render(); }
    });
  }

  /* ── Input box sync ── */
  if (inputMin) {
    inputMin.addEventListener('input', () => {
      const val = parseFloat(inputMin.value);
      if (!isNaN(val)) setPriceMin(val, true);
    });
    inputMin.addEventListener('change', () => {
      const val = parseFloat(inputMin.value) || PRICE_MIN;
      setPriceMin(val);
      render();
      scrollToGrid();
    });
  }

  if (inputMax) {
    inputMax.addEventListener('input', () => {
      const val = parseFloat(inputMax.value);
      if (!isNaN(val)) setPriceMax(val, true);
    });
    inputMax.addEventListener('change', () => {
      const val = parseFloat(inputMax.value) || PRICE_MAX;
      setPriceMax(val);
      render();
      scrollToGrid();
    });
  }

  updatePriceUI();

  /* ── Option clicks ── */
  function scrollToGrid() {
    if (window.innerWidth <= 749) return;
    const toggleBar = document.querySelector('.filter-toggle-bar');
    const target    = toggleBar || document.getElementById('filter-grid-inner');
    if (!target) return;
    const targetTop = target.getBoundingClientRect().top + window.scrollY;
    const offset    = getHeaderOffset() + 48; // header + gap above filter button
    window.scrollTo({ top: targetTop - offset, behavior: 'smooth' });
  }

  document.querySelectorAll('.filter-group__option').forEach(opt => {
    function selectOpt() {
      const groupName  = opt.closest('.filter-group').dataset.groupName;
      const filterName = opt.dataset.filterName;
      const selectedSet = activeFilters[groupName];
      if (selectedSet.has(filterName)) {
        selectedSet.delete(filterName);
      } else {
        selectedSet.add(filterName);
      }
      render();
      scrollToGrid();
    }
    opt.addEventListener('click', selectOpt);
    opt.addEventListener('keydown', e => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); selectOpt(); }
    });
  });

  if (clearBtn) {
    clearBtn.addEventListener('click', () => {
      Object.keys(activeFilters).forEach(k => { activeFilters[k] = new Set(); });
      priceMinVal = PRICE_MIN;
      priceMaxVal = PRICE_MAX;
      updatePriceUI();
      render();
      scrollToGrid();
    });
  }

  render();

  /* ── Group title click — expand/collapse options ──
     Same behaviour on all screen sizes: groups start collapsed
     (options have the hidden attribute in the Liquid) and the
     title button toggles them open/closed. ── */
  document.querySelectorAll('.filter-group').forEach(group => {
    const titleBtn   = group.querySelector('.filter-group__title');
    const panel      = group.querySelector('.filter-group__options, .filter-price');
    const isPriceGrp = group.dataset.groupName === 'Price';
    if (!titleBtn || !panel) return;

    titleBtn.addEventListener('click', () => {
      const isExpanded = group.classList.contains('is-expanded');

      if (isExpanded) {
        group.classList.remove('is-expanded');
        titleBtn.setAttribute('aria-expanded', 'false');
        panel.setAttribute('hidden', '');
      } else {
        group.classList.add('is-expanded');
        titleBtn.setAttribute('aria-expanded', 'true');
        panel.removeAttribute('hidden');
        if (isPriceGrp) updatePriceUI();
      }

      // Recalculate sticky layout since sidebar height just changed
      setTimeout(() => { cacheSidebarLayout(); updateStickyPosition(); }, 0);
    });
  });

  /* ── Sticky sidebar via fixed positioning ── */
  const sidebarInner = document.querySelector('.filter-sidebar__inner');

  function getHeaderOffset() {
    // Try common Shopify/Dawn sticky header selectors
    const header = document.querySelector(
      'sticky-header, .shopify-section-header-sticky, header.header, .site-header, #shopify-section-header'
    );
    if (header) {
      const h = header.getBoundingClientRect().height;
      if (h > 0) return h + 16; // header height + small gap
    }
    return 32; // fallback
  }

  let cachedLeft        = 0;
  let cachedWidth       = 0;
  let wrapperTop        = 0;
  let wrapperBottom     = 0;

  /* Guards the very first layout measurement. Until the page has fully
     loaded (images, fonts, etc.) wrapperTop can read far too small —
     e.g. before the hero image above this section has finished loading
     and pushed the filter bar down to its real position — which used
     to make updateStickyPosition() switch the sidebar to position:fixed
     with a near-zero top and briefly stack it on top of the hero.
     Until layoutReady is true, updateStickyPosition() always leaves the
     sidebar in normal document flow. */
  let layoutReady = false;

  function cacheSidebarLayout() {
    const wrapperEl = document.getElementById('collection-with-filters');
    if (!wrapperEl || !sidebarInner) return;
    // Temporarily reset to relative so we get the natural position
    const wasFixed = sidebarInner.style.position === 'fixed';
    if (wasFixed) {
      sidebarInner.style.position = 'relative';
      sidebarInner.style.top = '';
      sidebarInner.style.left = '';
      sidebarInner.style.width = '';
    }
    const rect      = wrapperEl.getBoundingClientRect();
    cachedLeft      = sidebarInner.getBoundingClientRect().left;
    cachedWidth     = sidebarInner.offsetWidth;
    // wrapperTop is the document-relative top of the collection wrapper
    wrapperTop      = rect.top + window.scrollY;
    wrapperBottom   = wrapperTop + wrapperEl.offsetHeight;
    if (wasFixed) updateStickyPosition();
  }

  /* ── Bottom boundary ─────────────────────────────────────────────────────
     Use the bottom of the last visible product card as the stopping point
     for the sticky sidebar. This ensures the sidebar + CTA box never extend
     below the product grid, regardless of what sections follow on the page.
     Uses [data-filter-names] rather than a specific <li> class name, since
     this script runs on two different grids that use different class names
     for their product cards (grid__item vs mp-grid__item) — both types of
     card carry a data-filter-names attribute, so this works on either grid.
     Falls back to the footer if no products are found.
  ──────────────────────────────────────────────────────────────────────── */
  function getFooterTop() {
    const grid = document.getElementById('product-grid');
    if (grid) {
      const items = [...grid.querySelectorAll('[data-filter-names]')]
        .filter(li => !li.hidden && li.style.display !== 'none');
      if (items.length) {
        return items[items.length - 1].getBoundingClientRect().bottom + window.scrollY;
      }
    }
    // Fallback to actual footer
    const footer = document.querySelector('footer, .footer, #footer, .site-footer');
    if (footer) return footer.getBoundingClientRect().top + window.scrollY;
    return Infinity;
  }

  function restoreSidebarFlow() {
    sidebarInner.style.position  = 'relative';
    sidebarInner.style.top       = '';
    sidebarInner.style.left      = '';
    sidebarInner.style.width     = '';
    sidebarInner.style.removeProperty('max-height');
    sidebarInner.style.removeProperty('overflow');
  }

  function updateStickyPosition() {
    if (!sidebarInner || !sidebarOpen) return;
    // No sticky on mobile — CSS handles the layout
    if (window.innerWidth <= 749) {
      sidebarInner.style.position = 'static';
      sidebarInner.style.top      = '';
      sidebarInner.style.left     = '';
      sidebarInner.style.width    = '';
      return;
    }

    // Don't switch to fixed positioning until we trust the measurements —
    // see the layoutReady comment above cacheSidebarLayout().
    if (!layoutReady) return;

    const scrollY  = window.scrollY;
    const OFFSET   = getHeaderOffset();
    // Use scrollHeight so we always get the full natural content height,
    // not the clamped value after max-height has been applied.
    const sidebarH = sidebarInner.scrollHeight;

    // The sidebar's natural top position relative to the viewport
    const naturalTop      = wrapperTop - scrollY;
    const effectiveOffset = scrollingUp ? OFFSET : 40;

    // Bottom boundary: bottom of last visible product card (viewport coords)
    // Recalculated every frame so it responds to filter changes immediately
    const visibleItems = productGrid
      ? [...productGrid.querySelectorAll('[data-filter-names]')]
          .filter(li => !li.hidden && li.style.display !== 'none')
      : [];
    const boundaryViewportTop = visibleItems.length
      ? visibleItems[visibleItems.length - 1].getBoundingClientRect().bottom
      : getFooterTop() - scrollY;

    if (naturalTop <= effectiveOffset) {
      // Sidebar would be hidden behind header — fix it to viewport
      if (sidebarInner.style.position !== 'fixed') {
        const rect  = sidebarInner.getBoundingClientRect();
        cachedLeft  = rect.left;
        cachedWidth = rect.width;
      }

      // maxFixedTop: highest the sidebar top can be so bottom stays above boundary
      const maxFixedTop    = boundaryViewportTop - sidebarH - 16;
      // maxScrolledTop: clamp sidebar so it doesn't go past its natural bottom
      const maxScrolledTop = boundaryViewportTop - sidebarH;

      const fixedTop        = Math.max(0, Math.min(effectiveOffset, maxScrolledTop, maxFixedTop));
      const availableHeight = boundaryViewportTop - fixedTop - 16;

      // If the last product has scrolled above where the sidebar would sit,
      // there is no valid position — restore sidebar to natural flow
      if (availableHeight <= 0) {
        restoreSidebarFlow();
        return;
      }

      sidebarInner.style.setProperty('max-height', `${availableHeight}px`, 'important');
      sidebarInner.style.setProperty('overflow',   'hidden',              'important');
      sidebarInner.style.position = 'fixed';
      sidebarInner.style.top      = `${fixedTop}px`;
      sidebarInner.style.left     = `${cachedLeft}px`;
      sidebarInner.style.width    = `${cachedWidth}px`;
    } else {
      // Sidebar is naturally in view — restore to flow
      restoreSidebarFlow();
    }
  }

  let rafPending   = false;
  let lastScrollY  = window.scrollY;
  let scrollingUp  = false;

  function onScroll() {
    const currentScrollY = window.scrollY;
    scrollingUp = currentScrollY < lastScrollY;
    lastScrollY = currentScrollY;

    if (!rafPending) {
      rafPending = true;
      requestAnimationFrame(() => {
        updateStickyPosition();
        rafPending = false;
      });
    }
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', () => {
    cacheSidebarLayout();
    updateStickyPosition();
  }, { passive: true });

  /* ── First layout measurement ──────────────────────────────
     Wait for the full page load (images, fonts, etc. — not just
     DOMContentLoaded) before trusting any measurement, then give
     the browser two animation frames to settle any final layout
     shift (e.g. web font swap) before caching positions and
     allowing the sticky logic to run. This is what stops the
     sidebar (price filter + "chat with us" box) from briefly
     jumping up and overlapping the hero image on load.
  ──────────────────────────────────────────────────────────── */
  function markLayoutReady() {
    cacheSidebarLayout();
    layoutReady = true;
    updateStickyPosition();
  }

  function onFullyLoaded() {
    requestAnimationFrame(function () {
      requestAnimationFrame(markLayoutReady);
    });
  }

  if (document.readyState === 'complete') {
    onFullyLoaded();
  } else {
    window.addEventListener('load', onFullyLoaded);
  }

})();